// This is the ONLY module in the app that should build a URL and call fetch() directly.
// Every backend call from any component/hook goes through the `api` object below so that
// auth headers, device-id headers, and error handling stay consistent in one place.
//
// BASE_URL selection: when the page itself is being served from localhost/127.0.0.1 (i.e.
// local dev, `npm start`), we talk to a separate API server (default :3001, override with
// REACT_APP_API_URL). In any other environment (staging/prod) BASE_URL is '' — an empty
// string means fetch() resolves paths against the current origin, so production assumes the
// frontend is served from the same origin/reverse-proxy as the API. This same rule is
// duplicated independently in hooks/usePulseEngine.js for the Socket.IO connection.
const BASE_URL = (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))
    ? (process.env.REACT_APP_API_URL || 'http://localhost:3001')
    : '';
/**
 * Gets or generates a stable per-browser device ID, cached in localStorage.
 * This is NOT a real hardware fingerprint — it's just a random string generated once and
 * reused, sent as `x-device-id` on every request so the backend's anti-fraud/rate-limiting
 * (see backend hardwareHash middleware) has something to key off besides the ghost token.
 * Clearing localStorage (or using a different browser) yields a brand new device ID.
 */
const getDeviceId = () => {
    let id = localStorage.getItem('x-device-id');
    if (!id) {
        id = `browser-${Math.random().toString(36).substring(2, 15)}-${Date.now()}`;
        localStorage.setItem('x-device-id', id);
    }
    return id;
};

/**
 * Shared fetch wrapper used by every method on the `api` object below.
 * - Reads the ghost session token fresh from localStorage on every call (not cached in a
 *   variable), so a login/logout elsewhere in the app is picked up immediately.
 * - Merges in the device-id header and any caller-supplied headers (caller headers win,
 *   e.g. admin endpoints override the Authorization header with an admin token).
 * - NOTE: every request/response body is logged to the console (tagged "DATA FLOW"), but only
 *   when `process.env.NODE_ENV !== 'production'` (see the `isDev` check below) — a production
 *   build never prints request/response bodies (which can include OTPs, admin credentials, or
 *   session/admin tokens) to the console. See audit notes.
 * - Throws a plain Error on any non-2xx response (message from the backend's `error`/
 *   `message` field) so callers can `.catch()` and show something to the user. If the
 *   response body isn't valid JSON, `response.json()` itself throws and that rejection is
 *   NOT caught here — it propagates to the caller as an unhandled-shaped error.
 */
const isDev = process.env.NODE_ENV !== 'production';

async function request(path, options = {}) {
    const token = localStorage.getItem('ghost_token');
    const deviceId = getDeviceId();

    const headers = {
        'Content-Type': 'application/json',
        'x-device-id': deviceId,
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
    };

    const requestId = Math.random().toString(36).substring(7);
    if (isDev) {
        console.log(`\n[DATA FLOW -> SERVER] [${requestId}] ${options.method || 'GET'} ${path}`);
        if (options.body) {
            console.log(`[DATA FLOW -> SERVER] [${requestId}] Payload:`, JSON.parse(options.body));
        }
    }

    const response = await fetch(`${BASE_URL}${path}`, {
        ...options,
        headers,
    });

    const data = await response.json();
    if (isDev) {
        console.log(`[SERVER -> DATA FLOW] [${requestId}] Response ${response.status}:`, data);
    }

    if (!response.ok) {
        if (isDev) {
            console.error(`[SERVER -> DATA FLOW] [${requestId}] Error Response:`, data);
        }
        throw new Error(data.error || data.message || 'Request failed');
    }

    return data;
}

export const api = {
    /**
     * Step 1: Request a one-time password.
     */
    requestOTP: (email) => request('/vault/ghost', {
        method: 'POST',
        body: JSON.stringify({ email }),
    }),

    /**
     * VANE Pre-flight Check: Scan user against poll requirements before authentication.
     */
    preflight: (email, pollId) => request('/vault/preflight', {
        method: 'POST',
        body: JSON.stringify({ email, pollId }),
    }),

    /**
     * Step 2: Verify the OTP and receive a session token.
     */
    verifyOTP: (email, otp, traits = {}) => request('/vault/verify', {
        method: 'POST',
        body: JSON.stringify({ email, otp, traits }),
    }),

    /**
     * Choice 2: Handshake with social provider data.
     */
    socialHandshake: (socialData) => request('/vault/social-handshake', {
        method: 'POST',
        body: JSON.stringify(socialData),
    }),

    /**
     * Atomic Silent Sync: Update traits in real-time.
     */
    updateTraits: (anonymousId, updates) => request('/vault/update-traits', {
        method: 'POST',
        body: JSON.stringify({ anonymousId, updates }),
    }),

    /**
     * The Final Seal: Promotions status to ACTIVE and grants welcome bonus.
     */
    finalizeGhost: (anonymousId) => request('/vault/finalize-ghost', {
        method: 'POST',
        body: JSON.stringify({ anonymousId }),
    }),

    /**
     * Cast a vote on a poll.
     */
    castVote: (pollId, choiceId, anonymousId, biometrics) => request('/api/vote', {
        method: 'POST',
        body: JSON.stringify({ pollId, choiceId, userId: anonymousId, biometrics }),
    }),

    /**
     * Get the ranked poll feed.
     */
    getFeed: (userId, page = 1, limit = 12) => {
        const params = new URLSearchParams();
        if (userId) params.append('userId', userId);
        params.append('page', page);
        params.append('limit', limit);
        return request(`/api/feed?${params.toString()}`);
    },

    getPollById: (id) => request(`/api/polls/${id}`),

    /**
     * Get user balance and streak (ledger-verified).
     * Returns: { userId, coins, balance, cached, ledgerComputed, streak, lastActive }
     */
    getUserBalance: (userId) => request(`/api/coins/balance/${userId}`),

    /**
     * Create a new poll.
     */
    createPoll: (pollData) => request('/api/polls', {
        method: 'POST',
        body: JSON.stringify(pollData),
    }),

    /**
     * Get all polls a user has voted on.
     */
    getVotedPolls: (userId) => request(`/api/user/voted/${userId}`),

    /**
     * Get all polls a user has created.
     */
    getCreatedPolls: (userId) => request(`/api/user/polls/${userId}`),

    /**
     * Get all polls a user has saved.
     */
    getSavedPolls: (userId) => request(`/api/user/saved/${userId}`),

    /**
     * Toggle the saved status of a poll for a user.
     */
    toggleSavedPoll: (pollId, userId) => request(`/api/user/save/${pollId}`, {
        method: 'POST',
        body: JSON.stringify({ userId })
    }),

    /**
     * Get results for a specific poll.
     */
    getResults: (pollId) => request(`/api/results/${pollId}`),

    /**
     * Get raw votes enriched with demographic data for the Insight Engine.
     */
    getInsights: (pollId) => request(`/api/insights/${pollId}`),

    /**
     * Unlock an expired poll's results using coins.
     */
    unlockPoll: (userId, pollId) => request('/api/user/unlock', {
        method: 'POST',
        body: JSON.stringify({ userId, pollId }),
    }),

    /**
     * Get global statistics.
     */
    getStats: () => request('/api/stats'),

    /**
     * VANE Progressive Gate: Check if user qualifies for a gated poll.
     * Returns { status, missing_criteria, entropy_reason }
     */
    gateCheck: (pollId, userId) => request('/vane/gate-check', {
        method: 'POST',
        body: JSON.stringify({ pollId, userId }),
    }),

    /**
     * Get recent news/verdicts.
     */
    getNews: () => request('/api/news'),

    /**
     * Send Pulse heartbeat
     */
    sendPulse: (userId, visiblePollIds) => request('/api/pulse', {
        method: 'POST',
        body: JSON.stringify({ userId, visiblePollIds }),
    }),


    /**
     * Get the full typed transaction history for a user, newest first.
     * Returns: { count, transactions: [{ amount, type, reason, reference_id, created }] }
     */
    getCoinHistory: (userId) => request(`/api/coins/history/${userId}`),

    /**
     * Spend coins on a named action (POLL_BOOST, DATA_UNLOCK, IDENTITY_SHIELD).
     * Returns: { status, type, amountSpent, newBalance, referenceId }
     */
    spendCoins: (userId, type, referenceId) => request('/api/coins/spend', {
        method: 'POST',
        body: JSON.stringify({ userId, type, referenceId }),
    }),

    /**
     * Get the total royalty coins earned by a specific user.
     * Returns: { userId, royalty }
     */
    getUserRoyalty: (userId) => request(`/api/user/royalty/${userId}`),

    /**
     * [TESTING ONLY] Get the full diagnostic data for a ghost.
     */
    getGhostDiag: (anonymousId) => request(`/vault/diag/${anonymousId}`),

    /**
     * Get user notifications
     */
    getNotifications: (userId, page = 1, size = 50) => request(`/api/notifications?userId=${userId}&page=${page}&size=${size}`),

    /**
     * Mark notification as read
     */
    markNotificationRead: (id) => request(`/api/notifications/${id}/read`, {
        method: 'PATCH'
    }),

    /**
     * Authenticate admin dashboard credentials
     */
    adminLogin: (username, password) => request('/api/admin/login', {
        method: 'POST',
        body: JSON.stringify({ username, password })
    }),

    /**
     * Get developer/admin dashboard analytics.
     * Uses a SEPARATE token (`admin_token`, sessionStorage) from the normal Ghost
     * `ghost_token` used everywhere else. Because `options.headers` is spread last inside
     * request(), this Authorization header overrides any ghost token that would otherwise be
     * attached — but if no admin_token exists yet, the call silently falls back to sending
     * the user's own ghost token (if any) instead of no auth at all.
     */
    getAdminDashboardStats: () => {
        const adminToken = sessionStorage.getItem('admin_token');
        return request('/api/admin/dashboard-stats', {
            headers: {
                ...(adminToken && { 'Authorization': `Bearer ${adminToken}` })
            }
        });
    },

    /**
     * Trigger database seeding of 100 polls
     */
    triggerAdminSeed: () => {
        const adminToken = sessionStorage.getItem('admin_token');
        return request('/api/admin/seed-trigger', {
            method: 'POST',
            headers: {
                ...(adminToken && { 'Authorization': `Bearer ${adminToken}` })
            }
        });
    },

    /**
     * Upload a media file (Perfect Image Management).
     * @param {File|Blob} file The image file to upload
     * @param {string} creatorId Optional ID of the user uploading
     * @param {string} purpose Optional purpose (poll_image, option_image)
     */
    uploadMedia: async (file, creatorId = '', purpose = 'poll_image') => {
        const formData = new FormData();
        formData.append('file', file);
        if (creatorId) formData.append('creator_id', creatorId);
        formData.append('purpose', purpose);

        const token = localStorage.getItem('ghost_token');
        const deviceId = getDeviceId();

        // Manual fetch because 'request' helper forces JSON content-type
        const response = await fetch(`${BASE_URL}/api/media`, {
            method: 'POST',
            body: formData,
            headers: {
                'x-device-id': deviceId,
                ...(token && { 'Authorization': `Bearer ${token}` }),
            }
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Upload failed');
        
        // PocketBase file URL format: /api/files/COLLECTION_ID/RECORD_ID/FILENAME
        // However, we can return the full object and let the frontend decide
        return data;
    },
};

export { getDeviceId };
